import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CaseDetail as CaseDetailType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** Mirrors c5-flowengine's own routes/cases.ts isOfficer() check. */
function isOfficerPersona(roles: string[]): boolean {
  return roles.some((r) => r === 'CASE_OFFICER' || r === 'CASE_SUPERVISOR' || r.endsWith('_OFFICER'));
}

export function CaseDetail() {
  const { ref } = useParams<{ ref: string }>();
  const { persona } = useAuth();
  const detailQ = useApiQuery<CaseDetailType>(ref ? `/api/v1/flow/cases/${ref}` : null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [payloadJson, setPayloadJson] = useState('{}');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const officer = isOfficerPersona(persona?.roles ?? []);

  const fire = async (action: string) => {
    setBusy(action);
    setActionError(null);
    try {
      let payload: unknown = {};
      try {
        payload = payloadJson.trim() ? JSON.parse(payloadJson) : {};
      } catch {
        throw new Error('Payload must be valid JSON');
      }
      await api.post(`/api/v1/flow/cases/${ref}/transition`, { action, payload });
      detailQ.reload();
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Transition failed',
      );
    } finally {
      setBusy(null);
    }
  };

  const submitComment = async () => {
    setBusy('comment');
    setActionError(null);
    try {
      await api.post(`/api/v1/flow/cases/${ref}/comment`, { text: comment });
      setComment('');
      detailQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Comment failed');
    } finally {
      setBusy(null);
    }
  };

  const assign = async (to: string | null) => {
    setBusy('assign');
    setActionError(null);
    try {
      await api.post(`/api/v1/flow/cases/${ref}/assign`, { assigneeId: to });
      detailQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Assign failed');
    } finally {
      setBusy(null);
    }
  };

  const checklistAction = async (code: string, status: 'MET' | 'WAIVED') => {
    setBusy(`cl-${code}`);
    setActionError(null);
    try {
      await api.post(`/api/v1/flow/cases/${ref}/checklist/${code}`, { status });
      detailQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Checklist update failed');
    } finally {
      setBusy(null);
    }
  };

  if (detailQ.loading) return <Spinner />;
  if (detailQ.error) return <ErrorNote message={detailQ.error} />;
  const c = detailQ.data;
  if (!c) return <EmptyState title="Case not found" />;

  return (
    <div>
      <PageHeader
        title={c.refNo}
        subtitle={`${c.flowCode} v${c.flowVersion} — ${c.applicantType.toLowerCase()} ${c.applicantId}`}
        actions={<StatusBadge status={c.state} />}
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card px-4 py-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assignee</div>
          <div className="mt-1 text-sm font-bold text-slate-900">{c.assigneeId ?? 'Unassigned'}</div>
        </div>
        <div className="card px-4 py-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">SLA due</div>
          <div className="mt-1 text-sm font-bold text-slate-900">{c.slaDueAt ? formatDateTime(c.slaDueAt) : '—'}</div>
        </div>
        <div className="card px-4 py-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Escalation</div>
          <div className="mt-1 text-sm font-bold text-slate-900">Level {c.escalationLevel}</div>
        </div>
        <div className="card px-4 py-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fee</div>
          <div className="mt-1 text-sm font-bold text-slate-900">
            <StatusBadge status={c.feeStatus} />
          </div>
        </div>
      </div>

      {officer ? (
        <div className="card mb-6 p-4">
          <h3 className="mb-2 text-sm font-bold text-slate-700">Assignment</h3>
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="field-input max-w-xs"
              placeholder="Officer personId"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            />
            <button className="btn-secondary text-xs" onClick={() => assign(assignee || null)} disabled={busy === 'assign'}>
              Assign
            </button>
            {persona?.sub ? (
              <button className="btn-secondary text-xs" onClick={() => assign(persona.sub)} disabled={busy === 'assign'}>
                Assign to me
              </button>
            ) : null}
            {c.assigneeId ? (
              <button className="btn-secondary text-xs" onClick={() => assign(null)} disabled={busy === 'assign'}>
                Unassign
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {!c.terminal && c.availableActions.length > 0 ? (
        <div className="card mb-6 p-4">
          <h3 className="mb-2 text-sm font-bold text-slate-700">Available actions</h3>
          <Field label="Payload (JSON — only needed if the action's guards require fields)">
            <textarea
              className="field-input font-mono text-xs"
              rows={2}
              value={payloadJson}
              onChange={(e) => setPayloadJson(e.target.value)}
            />
          </Field>
          <div className="mt-3 flex flex-wrap gap-2">
            {c.availableActions.map((a) => {
              const allowed = a.roles.some((r) => (persona?.roles ?? []).includes(r));
              return (
                <button
                  key={a.action}
                  className="btn-primary text-xs disabled:opacity-40"
                  disabled={!allowed || busy === a.action}
                  title={allowed ? `→ ${a.to}` : `Requires one of: ${a.roles.join(', ')}`}
                  onClick={() => fire(a.action)}
                >
                  {busy === a.action ? 'Working…' : `${a.action.replaceAll('_', ' ')} → ${a.to}`}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="mb-3 text-sm font-bold text-slate-700">Checklist</h3>
          {c.checklist.length === 0 ? (
            <p className="text-xs text-slate-400">No checklist items for the current state.</p>
          ) : (
            <div className="space-y-2">
              {c.checklist.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"
                >
                  <div>
                    <div className="text-sm">{item.label}</div>
                    <div className="text-[11px] text-slate-400">{item.code}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={item.status} />
                    {officer && item.status === 'PENDING' ? (
                      <>
                        <button
                          className="btn-success !px-2 !py-0.5 text-xs"
                          onClick={() => checklistAction(item.code, 'MET')}
                          disabled={busy === `cl-${item.code}`}
                        >
                          Met
                        </button>
                        <button
                          className="btn-secondary !px-2 !py-0.5 text-xs"
                          onClick={() => checklistAction(item.code, 'WAIVED')}
                          disabled={busy === `cl-${item.code}`}
                        >
                          Waive
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          <h3 className="mb-3 mt-6 text-sm font-bold text-slate-700">Referrals</h3>
          {c.referrals.length === 0 ? (
            <p className="text-xs text-slate-400">No cross-MDA referrals sent.</p>
          ) : (
            <div className="space-y-2">
              {c.referrals.map((r) => (
                <div key={r.id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {r.targetMda} — {r.service}
                    </span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400">
                    {r.slaDueAt ? `SLA due ${formatDateTime(r.slaDueAt)}` : null}
                    {r.respondedAt ? ` · responded ${formatDateTime(r.respondedAt)}` : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          {c.decisions.length > 0 ? (
            <>
              <h3 className="mb-3 mt-6 text-sm font-bold text-slate-700">Decisions</h3>
              <div className="space-y-2">
                {c.decisions.map((d) => (
                  <div key={d.id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <div className="font-medium">{d.outcome}</div>
                    <div className="text-[11px] text-slate-400">
                      signed by {d.signedBy} · {formatDateTime(d.createdAt)}
                      {d.noticeDocId ? ` · notice ${d.noticeDocId}` : ''}
                    </div>
                    {d.conditions.length > 0 ? (
                      <ul className="mt-1 list-disc pl-4 text-xs text-slate-500">
                        {d.conditions.map((cond, i) => (
                          <li key={i}>{cond}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {c.appeals.length > 0 ? (
            <>
              <h3 className="mb-3 mt-6 text-sm font-bold text-slate-700">Appeals</h3>
              <div className="space-y-2">
                {c.appeals.map((a) => (
                  <div key={a.id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{a.grounds.slice(0, 80)}</span>
                      <StatusBadge status={a.state} />
                    </div>
                    {a.tribunalRef ? <div className="text-[11px] text-slate-400">Tribunal ref {a.tribunalRef}</div> : null}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className="card p-4">
          <h3 className="mb-3 text-sm font-bold text-slate-700">Timeline</h3>
          <div className="max-h-[560px] space-y-2 overflow-y-auto">
            {c.timeline.map((ev) => (
              <div key={ev.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">{ev.type.replaceAll('_', ' ')}</span>
                  <span className="text-slate-400">{formatDateTime(ev.createdAt)}</span>
                </div>
                {ev.fromState || ev.toState ? (
                  <div className="mt-0.5 text-slate-500">
                    {ev.fromState ?? '—'} → {ev.toState ?? '—'}
                  </div>
                ) : null}
                <div className="mt-0.5 text-slate-400">by {ev.actorId}</div>
                {ev.payload ? (
                  <pre className="mt-1 overflow-x-auto rounded bg-white p-1.5 text-[10px] text-slate-500">
                    {JSON.stringify(ev.payload)}
                  </pre>
                ) : null}
              </div>
            ))}
          </div>

          {officer ? (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <Field label="Add a comment">
                <textarea className="field-input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
              </Field>
              <button
                className="btn-secondary mt-2 text-xs"
                onClick={submitComment}
                disabled={busy === 'comment' || comment.trim().length === 0}
              >
                {busy === 'comment' ? 'Posting…' : 'Post comment'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
